import { useState, useRef, useEffect, useCallback } from 'react';

const MUSIC_TRACKS = [
  { id: 'comet-man', name: 'Comet Man', path: '/comet-man.mp3' },
  { id: 'pixel-quest', name: 'Pixel Quest', path: '/pixel-quest.mp3' },
  { id: 'ancient-paths', name: 'Ancient Paths', path: '/ancient-paths.mp3' },
  { id: 'submerged-city', name: 'Submerged City', path: '/submerged-city.mp3' },
  { id: 'final-boss', name: 'Final Boss', path: '/final-boss.mp3' },
  { id: 'island-hopping', name: 'Island Hopping', path: '/island-hopping.mp3' },
  { id: 'jungle-serenity', name: 'Jungle Serenity', path: '/jungle-serenity.mp3' },
  { id: 'starlight-quest', name: 'Starlight Quest', path: '/starlight-quest.mp3' },
  { id: 'beach-side', name: 'Beach Side', path: '/beach-side.mp3' },
  { id: 'sunny-plaza-fiesta', name: 'Sunny Plaza Fiesta', path: '/sunny-plaza-fiesta.mp3' },
  { id: 'vibrant-town', name: 'Vibrant Town', path: '/vibrant-town.mp3' },
  { id: 'two-to-tango', name: 'Two to Tango', path: '/two-to-tango.mp3' },
  { id: 'polka-parade', name: 'Polka Parade', path: '/polka-parade.mp3' },
  { id: 'bolly-bounce', name: 'Bolly Bounce', path: '/bolly-bounce.mp3' }
];

const useMusicManager = (gameId = 'default') => {
  const audioRef = useRef(null);
  const [audioError, setAudioError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [currentTrack, setCurrentTrack] = useState(() => {
    const savedTrack = localStorage.getItem(`${gameId}_musicTrack`);
    return savedTrack ? JSON.parse(savedTrack) : MUSIC_TRACKS[0];
  });

  const [isMusicEnabled, setIsMusicEnabled] = useState(() => {
    const savedPreference = localStorage.getItem(`${gameId}_musicEnabled`);
    return savedPreference !== null ? JSON.parse(savedPreference) : true;
  });

  const [isContinuousPlay, setIsContinuousPlay] = useState(() => {
    const savedContinuous = localStorage.getItem(`${gameId}_continuousPlay`);
    return savedContinuous !== null ? JSON.parse(savedContinuous) : true;
  });

  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem(`${gameId}_musicVolume`);
    return savedVolume !== null ? parseFloat(savedVolume) : 0.5;
  });

  useEffect(() => {
    if (audioError) {
      const timer = setTimeout(() => setAudioError(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [audioError]);


  const createAudioElement = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.remove();
    }

    const audio = new Audio(currentTrack.path);
    audio.loop = !isContinuousPlay;
    audio.volume = volume;
    audioRef.current = audio;

    audio.addEventListener('error', () => setAudioError(true));
    audio.addEventListener('loadstart', () => setIsLoading(true));
    audio.addEventListener('canplaythrough', () => setIsLoading(false));

    if (isContinuousPlay) {
      audio.addEventListener('ended', () => {
        const currentIndex = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
        const nextIndex = (currentIndex + 1) % MUSIC_TRACKS.length;
        setCurrentTrack(MUSIC_TRACKS[nextIndex]);
        localStorage.setItem(`${gameId}_musicTrack`, JSON.stringify(MUSIC_TRACKS[nextIndex]));
      });
    }

    return audio;
  }, [currentTrack.path, currentTrack.id, volume, isContinuousPlay, gameId]);

  useEffect(() => {
    const audio = createAudioElement();

    if (isMusicEnabled && !audioError) {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          const startOnInteraction = () => {
            audio.play().catch(() => setAudioError(true));
            document.removeEventListener('click', startOnInteraction);
            document.removeEventListener('keydown', startOnInteraction);
          };
          document.addEventListener('click', startOnInteraction);
          document.addEventListener('keydown', startOnInteraction);
        });
      }
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.remove();
        audioRef.current = null;
      }
    };
  }, [currentTrack, createAudioElement, isMusicEnabled, audioError]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const toggleMusic = useCallback(() => {
    const newState = !isMusicEnabled;
    setIsMusicEnabled(newState);
    localStorage.setItem(`${gameId}_musicEnabled`, JSON.stringify(newState));

    if (audioRef.current && !audioError) {
      if (newState) {
        audioRef.current.play().catch(() => setAudioError(true));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isMusicEnabled, gameId, audioError]);

  const changeTrack = useCallback((track) => {
    if (track.id === currentTrack.id) return;

    setCurrentTrack(track);
    localStorage.setItem(`${gameId}_musicTrack`, JSON.stringify(track));
  }, [currentTrack.id, gameId]);

  const changeVolume = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    localStorage.setItem(`${gameId}_musicVolume`, clampedVolume.toString());
  }, [gameId]);

  const toggleContinuousPlay = useCallback(() => {
    const newState = !isContinuousPlay;
    setIsContinuousPlay(newState);
    localStorage.setItem(`${gameId}_continuousPlay`, JSON.stringify(newState));
  }, [isContinuousPlay, gameId]);

  const nextTrack = useCallback(() => {
    const currentIndex = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
    const nextIndex = (currentIndex + 1) % MUSIC_TRACKS.length;
    changeTrack(MUSIC_TRACKS[nextIndex]);
  }, [currentTrack.id, changeTrack]);

  const previousTrack = useCallback(() => {
    const currentIndex = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
    const prevIndex = (currentIndex - 1 + MUSIC_TRACKS.length) % MUSIC_TRACKS.length;
    changeTrack(MUSIC_TRACKS[prevIndex]);
  }, [currentTrack.id, changeTrack]);

  return {
    isMusicEnabled,
    toggleMusic,
    currentTrack,
    changeTrack,
    volume,
    changeVolume,
    isContinuousPlay,
    toggleContinuousPlay,
    nextTrack,
    previousTrack,
    tracks: MUSIC_TRACKS,
    audioError,
    isLoading,
  };
};

export default useMusicManager;